import { calculateSetVolume, estimateOneRepMax } from "./analytics.js";

// What the expanded exercise row shows, read straight from the imported
// workouts: the best set per metric and one row per session. The scope is the
// import window — the history fetch is capped, so a "best" only holds within
// what we fetched, never all time.

// Hevy sends 0 rather than null for a field a set never carried, so a
// bodyweight set arrives as weight 0 — real, but not a heaviest set.
function num(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

// Compared lexicographically: the first entry that differs decides.
const RANK = {
  weight: (set) => [num(set.weight), num(set.reps)],
  reps: (set) => [num(set.reps), num(set.weight)],
  oneRepMax: (set) => [estimateOneRepMax(set)],
  // The set a session row leads with: its heaviest estimate, falling back to
  // reps for bodyweight work, where every estimate is 0.
  top: (set) => [estimateOneRepMax(set), num(set.weight), num(set.reps)],
};

const QUALIFIES = {
  weight: (set) => num(set.weight) > 0 && num(set.reps) > 0,
  reps: (set) => num(set.reps) > 0,
  oneRepMax: (set) => estimateOneRepMax(set) > 0,
  // Reps are what makes a set showable: a duration or distance set has none, and
  // neither does a weight logged with reps 0.
  top: (set) => num(set.reps) > 0,
};

const BEST_KEYS = ["weight", "reps", "oneRepMax"];

// Same fallback as analytics' exercise frequency, so the name a row carries
// matches the workouts it came from.
function normalizeName(value) {
  return String(value || "Unknown exercise").trim() || "Unknown exercise";
}

function better(current, candidate, rank) {
  if (!current) return candidate;
  const a = rank(current.set);
  const b = rank(candidate.set);
  for (let i = 0; i < b.length; i += 1) {
    if (b[i] !== a[i]) return b[i] > a[i] ? candidate : current;
  }
  // Sessions are walked oldest first, so a tie keeps the day it was first hit.
  return current;
}

function describeSet(set) {
  return { weight: num(set.weight), reps: num(set.reps), oneRepMax: estimateOneRepMax(set) };
}

function describeBest(entry) {
  if (!entry) return null;
  return { ...describeSet(entry.set), date: entry.date, workoutId: entry.workoutId, workoutName: entry.workoutName };
}

export function buildExerciseDetail(workouts, name) {
  const target = normalizeName(name);
  const grouped = new Map();

  for (const workout of Array.isArray(workouts) ? workouts : []) {
    const time = Date.parse(workout?.startTime);
    // A session we cannot place in time can be neither ranked nor listed;
    // analytics drops those workouts too.
    if (!Number.isFinite(time)) continue;

    for (const exercise of workout.exercises || []) {
      if (normalizeName(exercise?.title) !== target) continue;
      const key = workout.id || workout.shortId || String(time);
      let session = grouped.get(key);
      if (!session) {
        session = {
          workoutId: workout.id || null,
          workoutName: String(workout.name || "").trim(),
          date: workout.startTime,
          time,
          sets: [],
        };
        grouped.set(key, session);
      }
      // One workout can log the same exercise twice; that is still one session,
      // so the entries merge.
      for (const set of Array.isArray(exercise.sets) ? exercise.sets : []) {
        if (set) session.sets.push(set);
      }
    }
  }

  const bests = { weight: null, reps: null, oneRepMax: null };
  const rows = [];
  let setCount = 0;

  for (const session of [...grouped.values()].sort((a, b) => a.time - b.time)) {
    const ref = { date: session.date, workoutId: session.workoutId, workoutName: session.workoutName };
    let topSet = null;
    let volumeKg = 0;
    let prSetCount = 0;

    for (const set of session.sets) {
      volumeKg += calculateSetVolume(set);
      if (set.isPersonalRecord) prSetCount += 1;
      if (QUALIFIES.top(set)) topSet = better(topSet, { set, ...ref }, RANK.top);
      for (const key of BEST_KEYS) {
        if (QUALIFIES[key](set)) bests[key] = better(bests[key], { set, ...ref }, RANK[key]);
      }
    }

    setCount += session.sets.length;
    rows.push({ ...ref, setCount: session.sets.length, volumeKg, prSetCount, topSet: topSet ? describeSet(topSet.set) : null });
  }

  return {
    name: target,
    sessions: rows.reverse(),
    sessionCount: rows.length,
    setCount,
    bests: Object.fromEntries(BEST_KEYS.map((key) => [key, describeBest(bests[key])])),
  };
}
